/* Header — le comportement de la barre d'en-tête, dans un seul fichier.

   Pourquoi un fichier à part plutôt qu'un bloc {% javascript %} dans
   sections/header.liquid : Shopify concatène les blocs de toutes les sections
   du thème dans un seul bundle (scripts.js), et le serveur de développement
   tronque ce bundle au-delà d'un certain poids. Quand la coupure tombe avant
   la définition des composants du header, le méga-menu et le repli au
   défilement cessent de fonctionner alors que rien n'a changé dans le code.
   Un fichier chargé en `defer` depuis layout/theme.liquid échappe à ce bundle,
   comme assets/cart.js pour le panier.

   Quatre web components, qui ne connaissent que des classes et des attributs
   `data-*` posés par sections/header.liquid :

   - <collapsing-header>  replie la barre quand la page défile vers le bas et
                          la redéploie quand elle remonte ;
   - <header-expand>      ouvre le méga-panneau de l'item de niveau 1 survolé
                          ou focalisé, et le referme (desktop) ;
   - <header-menu>        ouvre le menu mobile et y fait naviguer d'un niveau
                          à l'autre (sous 900px ; son contrat est plus bas) ;
   - <header-search>      ouvre le panneau de recherche et y affiche les
                          produits correspondant à la saisie (contrat plus bas).

   Un seul panneau ouvert à la fois : celui qui s'ouvre émet l'événement
   `header:panel` sur <collapsing-header>, avec lui-même en `detail`, et les
   autres se referment en l'entendant. L'événement part avant que le nouveau
   panneau ne pose son propre état : ce que les autres défont en se fermant
   (cadre au carré, page bloquée), il le refait juste après.

   Contrat de markup (ce que le script cherche dans le DOM) :

   - <collapsing-header> à l'intérieur du cadre `.header-group` (la section
     porte cette classe via son schéma). La classe `is-collapsed` est posée sur
     le cadre, et `is-header-collapsed` sur <html> pour les éléments sticky de
     la page. Sur le template produit (`html.template-product`), la barre est
     repliée par la feuille de style et le composant ne fait rien.
   - Les items de niveau 1 : `.header__menu .header__link`, avec `data-panel`
     = id du panneau à ouvrir (absent quand l'item n'a pas d'enfants) et
     `aria-expanded`, tenu à jour ici.
   - <header-expand> contient les panneaux `.header__panel` (attribut `hidden`
     quand fermés), chacun avec ses liens `.header__panel-link`.
   - `.header__actions a` : la première action (recherche), cible de Tab en
     sortant du dernier lien du dernier panneau. */

/* Repli au défilement. L'original compare chaque position de défilement à la
   précédente, sans seuil : on descend, la barre se replie ; on remonte, elle
   se redéploie. La classe va sur le cadre `.header-group` pour que critical.css
   compense la hauteur perdue par une marge basse et évite le saut de page ;
   l'élément lui-même sert de cible de repli. */
class CollapsingHeader extends HTMLElement {
  connectedCallback() {
    this.target = this.closest('.header-group') || this;
    this.expanded = false;

    /* La fiche produit replie la barre dans la feuille de style et la garde
       ainsi : rien à écouter. Poser la classe ici ajouterait aussi la marge
       basse qui compense un repli en cours, et décollerait la carte de 22px. */
    if (document.documentElement.classList.contains('template-product')) return;

    this.lastY = Math.max(0, Math.round(window.scrollY));
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    this.setCollapsed(false);
  }

  onScroll() {
    const y = Math.max(0, Math.round(window.scrollY));
    if (y === this.lastY) return;
    /* L'original ne replie jamais la barre tant qu'un panneau est ouvert. */
    if (!this.expanded) this.setCollapsed(y > this.lastY);
    this.lastY = y;
  }

  /* Le cadre porte l'état pour son propre style, et la racine du document le
     porte pour tout le reste : `--sticky-offset-top` est la distance à laquelle
     un élément sticky dégage le cadre, elle doit donc diminuer avec lui. Sans
     cela, chaque titre sticky de la page garde l'écart de la barre haute et
     flotte trop bas une fois la barre repliée. */
  setCollapsed(state) {
    this.target.classList.toggle('is-collapsed', state);
    document.documentElement.classList.toggle('is-header-collapsed', state);
  }

  /* Appelé par <header-expand> : un panneau ouvert redéploie la barre et met
     le cadre au carré (critical.css, `.is-expanded`) tant qu'il reste ouvert. */
  setExpanded(state) {
    this.expanded = state;
    this.target.classList.toggle('is-expanded', state);
    if (state) this.setCollapsed(false);
  }
}

/* Méga-panneaux : ouvrir le panneau de l'item de niveau 1 survolé (ou
   focalisé), fermer au survol d'un item sans panneau ou quand le pointeur
   quitte la barre et le panneau, d'un coup et sans transition, comme
   l'original. Clavier : le focus ouvre, Échap ferme et rend le focus à l'item. */
class HeaderExpand extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('collapsing-header') || this.parentElement;
    this.current = null;
    this.root.addEventListener('mouseover', this.onEnter);
    this.root.addEventListener('focusin', this.onEnter);
    this.root.addEventListener('mouseleave', this.close);
    this.root.addEventListener('focusout', this.onFocusOut);
    this.root.addEventListener('keydown', this.onKeyDown);
    this.root.addEventListener('header:panel', this.onPanel);
  }

  disconnectedCallback() {
    this.root.removeEventListener('header:panel', this.onPanel);
    this.root.removeEventListener('mouseover', this.onEnter);
    this.root.removeEventListener('focusin', this.onEnter);
    this.root.removeEventListener('mouseleave', this.close);
    this.root.removeEventListener('focusout', this.onFocusOut);
    this.root.removeEventListener('keydown', this.onKeyDown);
    this.close();
  }

  /* Pointeur : seuls les items du menu agissent (survoler le logo ou les
     actions laisse le panneau ouvert, comme sur l'original). Clavier : un
     focus qui se pose ailleurs dans la barre (recherche, compte, panier, logo)
     ferme le panneau, pour que Tab continue vers la page au lieu de retomber
     dans le panneau. */
  onEnter = (event) => {
    /* Le panneau de recherche ouvert, le survol du menu ne l'écrase pas : on
       peut passer sur les items en allant au champ. Le focus clavier, lui,
       ouvre toujours le panneau de l'item. */
    if (event.type === 'mouseover') {
      const search = this.root.querySelector('header-search');
      if (search && search.isOpen) return;
    }
    const link = event.target.closest('.header__menu .header__link');
    if (!link) {
      if (event.type === 'focusin' && !event.target.closest('.header__panel')) this.close();
      return;
    }
    if (event.type === 'focusin' && link === this.suppress) {
      this.suppress = null;
      return;
    }
    if (link.dataset.panel) this.open(link);
    else this.close();
  };

  onFocusOut = (event) => {
    if (!this.root.contains(event.relatedTarget)) this.close();
  };

  onPanel = (event) => {
    if (event.detail !== this) this.close();
  };

  /* Échap ferme et rend le focus à l'item. Tab est redirigé parce que les
     panneaux sont placés après la barre dans le DOM : depuis l'item ouvert,
     Tab entre dans son panneau ; depuis le dernier lien du panneau, il passe à
     l'item suivant (ou à la première action) ; Maj+Tab depuis le premier lien
     du panneau revient à l'item. */
  onKeyDown = (event) => {
    if (!this.current) return;
    if (event.key === 'Escape') {
      const link = this.current;
      this.close();
      /* Rendre le focus à l'item déclenche un `focusin`, que `onEnter` lit
         comme une demande d'ouverture : sans cette garde, le panneau se
         rouvrait aussitôt et il fallait un second Échap. */
      this.suppress = link;
      link.focus();
      /* `focusin` est synchrone : passé `focus()`, la garde a servi ou n'a
         pas eu à servir (focus déjà sur l'item) ; on ne la laisse pas
         traîner, elle avalerait la prochaine arrivée au clavier. */
      this.suppress = null;
      return;
    }
    if (event.key !== 'Tab') return;
    /* Les images liées du panneau (`.header__media-link`) sont des liens comme
       les autres : elles viennent en dernier, après les colonnes. */
    const links = this.panelOf(this.current).querySelectorAll('.header__panel-link, .header__media-link');
    if (!links.length) return;
    const target = event.target;
    let next = null;
    if (target === this.current && !event.shiftKey) next = links[0];
    else if (target === links[0] && event.shiftKey) next = this.current;
    else if (target === links[links.length - 1] && !event.shiftKey) next = this.after(this.current);
    if (!next) return;
    event.preventDefault();
    next.focus();
  };

  after(link) {
    return link.nextElementSibling || this.root.querySelector('.header__actions a');
  }

  panelOf(link) {
    return document.getElementById(link.dataset.panel);
  }

  open(link) {
    if (this.current === link) return;
    const panel = this.panelOf(link);
    if (!panel) return;
    if (!this.current) this.root.dispatchEvent(new CustomEvent('header:panel', { detail: this }));
    if (this.current) {
      this.current.setAttribute('aria-expanded', 'false');
      this.panelOf(this.current).hidden = true;
    }
    this.current = link;
    link.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.hidden = false;
    if (this.root.setExpanded) this.root.setExpanded(true);
  }

  close = () => {
    if (!this.current) return;
    this.current.setAttribute('aria-expanded', 'false');
    this.panelOf(this.current).hidden = true;
    this.current = null;
    this.hidden = true;
    if (this.root.setExpanded) this.root.setExpanded(false);
  };
}

/* Menu mobile (sous 900px). Le bouton « Menu » de la barre ouvre un panneau
   suspendu sous la barre, qui montre un niveau du menu à la fois : cliquer un
   item qui a des enfants remplace la liste par la leur, précédée d'une rangée
   de retour ; le retour remonte d'un niveau. Tout est rendu par Liquid, le
   script ne fait qu'afficher et masquer, d'un coup, sans transition.

   Pendant que le panneau est ouvert, comme sur l'original :
   - la page ne défile plus (`overflow: hidden` sur <html>) et un voile blanc
     flouté la couvre ; toucher le voile ferme le menu ;
   - la barre se redéploie et ne se replie plus (`setExpanded` du
     <collapsing-header>, qui met aussi le cadre au carré et masque les liens
     rapides) ;
   - le panneau s'arrête une gouttière au-dessus du bas de la fenêtre et défile
     au-delà : sa hauteur maximale est mesurée à l'ouverture.

   Clavier : Échap ferme et rend le focus au bouton ; en changeant de niveau,
   le focus passe à la rangée de retour du niveau ouvert, et en revenant, au
   bouton qui l'avait ouvert.

   Contrat de markup (sections/header.liquid) :
   - `[data-header-menu-toggle]` dans la barre : le bouton, avec
     `aria-controls` = id du panneau et `aria-expanded`, tenu à jour ici (le
     CSS en tire `=` / « Menu » ou `X` / « Close ») ;
   - <header-menu> contient le voile `[data-header-menu-close]` (déplacé dans
     <body> à la connexion, voir plus bas) et le panneau (id ci-dessus,
     attribut `hidden` quand fermé) ;
   - dans le panneau, des listes `.header__level` : la première est le niveau
     1, les autres portent `hidden` et `data-parent` = id de la liste parente ;
   - `[data-level-open="<id>"]` ouvre la liste de cet id, `[data-level-back]`
     remonte à la parente. */
class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('collapsing-header');
    this.toggle = this.root && this.root.querySelector('[data-header-menu-toggle]');
    this.drawer = this.querySelector('.header__drawer');
    this.overlay = this.querySelector('[data-header-menu-close]');
    if (!this.toggle || !this.drawer) return;

    /* Le voile quitte le header pour <body>. Le cadre est collant avec un
       z-index de 500 : tout ce qu'il contient est peint à ce niveau-là, donc un
       voile laissé à l'intérieur couvrirait aussi la bannière de notification
       (470), que l'original laisse nette au-dessus de son voile (450). Placé
       directement dans <body>, il retrouve l'ordre de l'original : page, voile,
       bannière, header. */
    if (this.overlay) document.body.append(this.overlay);

    this.mobile = window.matchMedia('screen and (max-width: 900px)');
    this.toggle.addEventListener('click', this.onToggle);
    this.drawer.addEventListener('click', this.onDrawerClick);
    this.overlay.addEventListener('click', this.onOverlayClick);
    this.mobile.addEventListener('change', this.onBreakpoint);
    this.root.addEventListener('header:panel', this.onPanel);
    this.root.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    if (!this.toggle) return;
    this.close();
    this.toggle.removeEventListener('click', this.onToggle);
    this.drawer.removeEventListener('click', this.onDrawerClick);
    this.overlay.removeEventListener('click', this.onOverlayClick);
    this.mobile.removeEventListener('change', this.onBreakpoint);
    this.root.removeEventListener('header:panel', this.onPanel);
    this.root.removeEventListener('focusout', this.onFocusOut);
    /* Section re-rendue dans l'éditeur : le nouveau header apporte son voile. */
    this.overlay.remove();
  }

  get isOpen() {
    return !this.drawer.hidden;
  }

  /* Le panneau de recherche ouvert depuis le menu garde le bouton en « X » /
     « Close » : le bouton le referme, comme sur l'original. */
  onToggle = () => {
    const search = this.root.querySelector('header-search');
    /* `restoreFocus` : sur iOS, toucher un bouton ne lui donne pas le focus,
       qui retomberait sur <body> avec le champ disparu ; l'ouvreur du panneau
       étant masqué en mobile, c'est ce bouton-ci qui le reçoit. */
    if (search && search.isOpen) search.close({ restoreFocus: true });
    else if (this.isOpen) this.close();
    else this.open();
  };

  /* Toucher le voile ferme ; le focus revient au bouton, pour ne pas le
     laisser sur <body> ou sur une rangée devenue invisible. */
  onOverlayClick = () => {
    this.close();
    this.toggle.focus();
  };

  onPanel = (event) => {
    if (event.detail !== this) this.close();
  };

  /* Tab qui sort du header (après la dernière rangée) ferme le menu, comme
     le panneau de recherche : la page bloquée sous le voile n'a pas à
     recevoir le focus, où il serait flouté et illisible. Un clic dans le
     vide (`relatedTarget` nul) ne compte pas : le voile s'en charge. */
  onFocusOut = (event) => {
    if (!this.isOpen || !event.relatedTarget) return;
    if (!this.root.contains(event.relatedTarget)) this.close();
  };

  /* Passer en desktop menu ouvert laisserait la page bloquée sous un voile. */
  onBreakpoint = (event) => {
    if (!event.matches) this.close();
  };

  onDrawerClick = (event) => {
    const opener = event.target.closest('[data-level-open]');
    if (opener) {
      this.showLevel(opener.dataset.levelOpen);
      const back = this.currentLevel().querySelector('[data-level-back]');
      if (back) back.focus();
      return;
    }
    if (event.target.closest('[data-level-back]')) {
      const from = this.currentLevel();
      this.showLevel(from.dataset.parent);
      const opener = this.drawer.querySelector(`[data-level-open="${from.id}"]`);
      if (opener) opener.focus();
    }
  };

  onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
    this.toggle.focus();
  };

  levels() {
    return this.drawer.querySelectorAll('.header__level');
  }

  currentLevel() {
    return [...this.levels()].find((level) => !level.hidden);
  }

  showLevel(id) {
    const target = document.getElementById(id);
    if (!target) return;
    this.levels().forEach((level) => {
      level.hidden = level !== target;
    });
    this.drawer.scrollTop = 0;
  }

  /* La place sous le panneau : du haut du panneau au bas de la fenêtre, moins
     la gouttière et la bordure basse. Mesurée une fois le panneau affiché, la
     barre redéployée — la bannière de notification peut être encore à l'écran
     ou déjà partie selon le défilement. */
  measure = () => {
    if (!this.isOpen) return;
    const gutter = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gutter')) || 10;
    const top = this.drawer.getBoundingClientRect().top;
    this.drawer.style.setProperty('--header-menu-max-height', `${Math.max(0, window.innerHeight - top - gutter - 1)}px`);
  };

  open() {
    if (this.isOpen) return;
    this.root.dispatchEvent(new CustomEvent('header:panel', { detail: this }));
    this.showLevel(this.levels()[0].id);
    this.drawer.hidden = false;
    this.overlay.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    if (this.root.setExpanded) this.root.setExpanded(true);
    document.documentElement.style.overflow = 'hidden';
    /* Le cadre se redéploie avec une transition : on mesure après elle, et
       encore à chaque changement de taille de la fenêtre (clavier virtuel,
       barre d'adresse du navigateur mobile). */
    this.measure();
    this.measureTimer = setTimeout(this.measure, 250);
    window.addEventListener('resize', this.measure);
    document.addEventListener('keydown', this.onKeyDown);
    /* Le focus entre dans le panneau : sans cela, Tab depuis le bouton
       passait par le logo et le panier avant d'atteindre le menu ouvert. Un
       focus posé après un toucher ne dessine pas d'anneau (`:focus-visible`
       ne s'allume qu'au clavier). */
    const first = this.drawer.querySelector('.header__drawer-search, .header__row');
    if (first) first.focus({ preventScroll: true });
  }

  close = () => {
    if (!this.isOpen) return;
    this.drawer.hidden = true;
    this.overlay.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    if (this.root.setExpanded) this.root.setExpanded(false);
    document.documentElement.style.overflow = '';
    clearTimeout(this.measureTimer);
    window.removeEventListener('resize', this.measure);
    document.removeEventListener('keydown', this.onKeyDown);
  };
}

/* Panneau de recherche (snippets/search-panel.liquid). Le lien « Search »
   des actions, et en mobile le bouton gris en tête du menu, ouvrent un
   panneau blanc suspendu sous la barre : un grand champ, puis une bande de
   vignettes produit. Tout est rendu par Liquid ; sans JavaScript, le lien
   mène simplement à la page de recherche.

   Pendant la frappe, la bande de vignettes est remplacée par les produits
   correspondant à la saisie (recherche prédictive, absente de l'original,
   qui garde ses suggestions fixes) ; champ vidé, les suggestions choisies
   dans les réglages du thème reviennent. Voir `suggest`.

   Pendant que le panneau est ouvert, comme sur l'original :
   - le focus est dans le champ ; Entrée soumet le formulaire, donc charge la
     page de recherche, sans rien de particulier ici ;
   - la page ne défile plus et un voile blanc flouté la couvre ; toucher le
     voile, recliquer le lien ou appuyer sur Échap ferme le panneau ;
   - la barre se redéploie et ne se replie plus (`setExpanded`) ;
   - en mobile, le bouton du menu reste en « X » / « Close » et referme le
     panneau (voir `onToggle` de <header-menu>).

   Contrat de markup :
   - `[data-search-open]` dans <collapsing-header> : les déclencheurs, avec
     `aria-controls` et `aria-expanded`, tenu à jour ici ;
   - <header-search> contient le voile `[data-search-close]` (déplacé dans
     <body>, comme celui du menu), le panneau `.search-panel` (attribut
     `hidden` quand fermé) et le champ `[data-search-input]` ;
   - `data-suggest-url` (routes.predictive_search_url, préfixe de langue
     compris) et `data-section` (la section qui rend les vignettes) sur
     <header-search> ;
   - `[data-search-visual]` : le bloc titre + bande, masqué quand vide ;
     `[data-search-results]` : ce qui est remplacé ; `[data-search-status]` :
     la zone lue par les lecteurs d'écran (nombre de produits) ;
   - la réponse de la section contient `[data-predictive-results]`, dont le
     contenu remplace celui de `[data-search-results]` et dont
     `data-status` va dans la zone de statut. */
class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('collapsing-header');
    this.panel = this.querySelector('.search-panel');
    this.overlay = this.querySelector('[data-search-close]');
    this.input = this.querySelector('[data-search-input]');
    if (!this.root || !this.panel || !this.input) return;

    this.openers = this.root.querySelectorAll('[data-search-open]');
    this.toggle = this.root.querySelector('[data-header-menu-toggle]');
    /* Le bouton du menu désigne le menu par `aria-controls` ; pendant que
       c'est la recherche qu'il referme (il passe en « X »), il doit désigner
       le panneau de recherche. On garde l'id du menu pour le rétablir. */
    this.toggleControls = this.toggle ? this.toggle.getAttribute('aria-controls') : null;

    /* Le lien « Search » se comporte en bouton dès que le script tourne : un
       `role="button"` le dit au lecteur d'écran (un lien « réduit » ne veut
       rien dire) et Espace l'active comme un bouton. Le `href` reste : sans
       JavaScript, le lien mène à la page de recherche. */
    this.openers.forEach((opener) => {
      if (opener.tagName !== 'A') return;
      opener.setAttribute('role', 'button');
      opener.addEventListener('keydown', this.onOpenerKeyDown);
    });
    this.visual = this.querySelector('[data-search-visual]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');

    /* Les suggestions choisies dans les réglages, gardées telles que Liquid
       les a rendues pour revenir quand le champ se vide ; puis les réponses
       déjà reçues, par termes, pour ne pas redemander ce qu'un effacement
       fait repasser. */
    this.defaultMarkup = this.results ? this.results.innerHTML : '';
    this.cache = new Map();
    this.query = '';

    /* Même raison que pour le voile du menu : laissé dans le cadre collant, il
       serait peint au niveau du cadre et flouterait la bannière. */
    if (this.overlay) document.body.append(this.overlay);

    this.openers.forEach((opener) => opener.addEventListener('click', this.onOpenerClick));
    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
    this.input.addEventListener('input', this.onInput);
    this.root.addEventListener('header:panel', this.onPanel);
    this.root.addEventListener('focusout', this.onFocusOut);
  }

  disconnectedCallback() {
    if (!this.openers) return;
    this.close();
    this.openers.forEach((opener) => {
      opener.removeEventListener('click', this.onOpenerClick);
      opener.removeEventListener('keydown', this.onOpenerKeyDown);
    });
    this.input.removeEventListener('input', this.onInput);
    clearTimeout(this.inputTimer);
    if (this.request) this.request.abort();
    this.root.removeEventListener('header:panel', this.onPanel);
    this.root.removeEventListener('focusout', this.onFocusOut);
    /* Section re-rendue dans l'éditeur : le nouveau header apporte son voile. */
    if (this.overlay) this.overlay.remove();
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  onOpenerClick = (event) => {
    event.preventDefault();
    if (this.isOpen) this.close({ restoreFocus: true });
    else this.open(event.currentTarget);
  };

  onOpenerKeyDown = (event) => {
    if (event.key !== ' ') return;
    event.preventDefault();
    event.currentTarget.click();
  };

  /* Toucher le voile ferme ; le focus revient au déclencheur, pour ne pas le
     laisser sur <body> ou dans un champ disparu. */
  onOverlayClick = () => {
    this.close({ restoreFocus: true });
  };

  onPanel = (event) => {
    if (event.detail !== this) this.close();
  };

  /* Tab qui sort du header (après la dernière vignette) ferme le panneau,
     comme le méga-panneau : la page sous le voile n'a pas à recevoir le focus
     pendant qu'il est ouvert. Un clic dans le vide (`relatedTarget` nul) ne
     compte pas : le voile s'en charge. */
  onFocusOut = (event) => {
    if (!this.isOpen || !event.relatedTarget) return;
    if (!this.root.contains(event.relatedTarget)) this.close();
  };

  onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.close({ restoreFocus: true });
  };

  /* Recherche prédictive. Chaque frappe relance un délai de 200 ms : la
     requête ne part qu'une fois l'internaute arrêté, pas à chaque lettre. */
  onInput = () => {
    clearTimeout(this.inputTimer);
    this.inputTimer = setTimeout(() => this.suggest(), 200);
  };

  /* Demande à Shopify les produits correspondant aux termes, rendus par
     sections/predictive-search.liquid, et remplace la bande de vignettes.
     La requête précédente encore en route est annulée : sans cela, une
     réponse lente à « ju » pourrait arriver après celle de « jupe » et
     l'écraser. Champ vide : retour aux suggestions des réglages. */
  async suggest() {
    const query = this.input.value.trim();
    if (query === this.query) return;
    this.query = query;
    if (this.request) this.request.abort();
    if (this.results) this.results.removeAttribute('aria-busy');

    if (!query) {
      this.render(this.defaultMarkup, '');
      return;
    }
    if (this.cache.has(query)) {
      this.render(...this.cache.get(query));
      return;
    }

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '10',
      section_id: this.dataset.section,
    });
    this.request = new AbortController();
    if (this.results) this.results.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.dataset.suggestUrl}?${params}`, { signal: this.request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const found = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-results]');
      if (!found) return;
      const entry = [found.innerHTML, found.dataset.status || ''];
      this.cache.set(query, entry);
      /* Les termes ont pu changer pendant le trajet (requête non annulée
         parce que déjà reçue) : on n'affiche que la réponse à la saisie
         courante. */
      if (query === this.query) this.render(...entry);
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* Réseau coupé ou erreur du serveur : la bande reste telle quelle, et
         Entrée mène toujours à la page de recherche. */
      console.error(error);
    } finally {
      if (this.results && query === this.query) this.results.removeAttribute('aria-busy');
    }
  }

  /* Remplacement d'un coup, sans transition. Le bloc des suggestions reste
     masqué tant qu'il n'a rien à montrer (aucun produit choisi dans les
     réglages et champ vide). La bande revient au début : une nouvelle liste
     ne s'ouvre pas là où l'ancienne avait été défilée. */
  render(markup, status) {
    if (!this.results) return;
    this.results.innerHTML = markup;
    if (this.visual) this.visual.hidden = !this.results.children.length;
    if (this.status) this.status.textContent = status;
  }

  /* L'événement part avant tout le reste : le menu mobile, s'il était
     ouvert, se ferme et rend la page, puis le panneau la reprend. */
  open(opener) {
    if (this.isOpen) return;
    this.root.dispatchEvent(new CustomEvent('header:panel', { detail: this }));
    this.opener = opener;
    this.panel.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    this.openers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-controls', this.panel.id || this.toggleControls);
    }
    if (this.root.setExpanded) this.root.setExpanded(true);
    /* La page bloquée garde la place de sa barre de défilement : sans
       `scrollbar-gutter`, tout sauterait de sa largeur vers la droite. */
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.scrollbarGutter = 'stable';
    document.addEventListener('keydown', this.onKeyDown);

    /* Le focus dans le champ, tout de suite : sur mobile, seul un focus posé
       pendant le clic ouvre le clavier. Puis de nouveau à l'image suivante si
       quelque chose l'a repris entre-temps (le lien cliqué, la fermeture du
       menu mobile). L'attribut `autofocus` du champ ne suffit pas : il n'agit
       qu'au chargement de la page, quand le panneau est encore masqué. */
    this.focusInput();
    requestAnimationFrame(() => {
      if (this.isOpen && document.activeElement !== this.input) this.focusInput();
    });
    /* Champ prérempli : la bande montre d'emblée les produits de ces termes. */
    this.suggest();
  }

  /* Le curseur en fin de saisie : sur la page de recherche, le champ arrive
     prérempli avec les termes cherchés. */
  focusInput() {
    this.input.focus({ preventScroll: true });
    const end = this.input.value.length;
    try {
      this.input.setSelectionRange(end, end);
    } catch (error) {
      /* Certains navigateurs refusent la sélection sur un champ `search`. */
    }
  }

  /* `restoreFocus` rend le focus au déclencheur — ou, s'il a disparu avec le
     menu mobile, au bouton du menu. */
  close({ restoreFocus = false } = {}) {
    if (!this.isOpen) return;
    this.panel.hidden = true;
    if (this.overlay) this.overlay.hidden = true;
    this.openers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this.toggleControls) this.toggle.setAttribute('aria-controls', this.toggleControls);
    }
    if (this.root.setExpanded) this.root.setExpanded(false);
    document.documentElement.style.overflow = '';
    document.documentElement.style.scrollbarGutter = '';
    document.removeEventListener('keydown', this.onKeyDown);

    if (!restoreFocus) return;
    const target = this.opener && this.opener.offsetParent ? this.opener : this.toggle;
    if (target) target.focus();
  }
}

if (!customElements.get('collapsing-header')) {
  customElements.define('collapsing-header', CollapsingHeader);
}
if (!customElements.get('header-expand')) {
  customElements.define('header-expand', HeaderExpand);
}
if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch);
}
